/**
 * @module services/collectionActionsService
 * @description High-level collection action service
 *
 * Provides user-facing collection operations with:
 * - Notification feedback
 * - Error handling
 * - Tree manipulation utilities
 *
 * @example
 * import { collectionActions } from '@services/collectionActionsService';
 *
 * // Create a new collection
 * await collectionActions.createCollection(libraryId, 'user', 'My Papers');
 *
 * // Rename a collection
 * await collectionActions.renameCollection(libraryId, 'user', collection, 'New Name');
 */

import { createLogger } from './loggerService';
import { notify } from './notificationService';
import { zoteroService } from './zoteroService';
import type { Collection, LibraryType, ServiceResult, ActionResult } from '@types';

const log = createLogger('collection');

// =============================================================================
// TYPES
// =============================================================================

/** Collection with computed properties */
export interface CollectionNode extends Collection {
  /** Child collections */
  children: CollectionNode[];
  /** Nesting level */
  level: number;
  /** Whether expanded in tree */
  expanded?: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Build collection tree from flat list
 */
export function buildCollectionTree(collections: Collection[]): CollectionNode[] {
  const map = new Map<string, CollectionNode>();
  const roots: CollectionNode[] = [];

  // First pass: create nodes
  for (const collection of collections) {
    map.set(collection.key, {
      ...collection,
      children: [],
      level: 0,
    });
  }

  // Second pass: build tree
  for (const collection of collections) {
    const node = map.get(collection.key)!;
    const parentKey = collection.data?.parentCollection;

    if (parentKey && map.has(parentKey)) {
      const parent = map.get(parentKey)!;
      node.level = parent.level + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort alphabetically
  const sortChildren = (nodes: CollectionNode[]): void => {
    nodes.sort((a, b) => a.data.name.localeCompare(b.data.name));
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };

  sortChildren(roots);
  return roots;
}

/**
 * Flatten collection tree for rendering
 */
export function flattenCollectionTree(
  nodes: CollectionNode[],
  expandedKeys: Set<string> = new Set()
): CollectionNode[] {
  const result: CollectionNode[] = [];

  const traverse = (nodes: CollectionNode[]): void => {
    for (const node of nodes) {
      result.push(node);
      if (expandedKeys.has(node.key) && node.children.length > 0) {
        traverse(node.children);
      }
    }
  };

  traverse(nodes);
  return result;
}

/**
 * Find collection in tree by key
 */
export function findCollectionInTree(nodes: CollectionNode[], key: string): CollectionNode | null {
  for (const node of nodes) {
    if (node.key === key) return node;
    const found = findCollectionInTree(node.children, key);
    if (found) return found;
  }
  return null;
}

/**
 * Get all ancestor keys for a collection
 */
export function getAncestorKeys(collections: Collection[], key: string): string[] {
  const ancestors: string[] = [];
  const map = new Map(collections.map((c) => [c.key, c]));

  let current = map.get(key);
  while (current?.data?.parentCollection) {
    ancestors.push(current.data.parentCollection);
    current = map.get(current.data.parentCollection);
  }

  return ancestors;
}

/**
 * Get all descendant keys for a collection
 */
export function getDescendantKeys(collections: Collection[], key: string): string[] {
  const descendants: string[] = [];
  const childMap = new Map<string, string[]>();

  // Build child map
  for (const collection of collections) {
    const parentKey = collection.data?.parentCollection;
    if (parentKey) {
      const children = childMap.get(parentKey) || [];
      children.push(collection.key);
      childMap.set(parentKey, children);
    }
  }

  // Collect descendants recursively
  const collect = (parentKey: string): void => {
    const children = childMap.get(parentKey) || [];
    for (const childKey of children) {
      descendants.push(childKey);
      collect(childKey);
    }
  };

  collect(key);
  return descendants;
}

// =============================================================================
// COLLECTION ACTIONS SERVICE
// =============================================================================

/**
 * Collection Actions Service
 */
class CollectionActionsService {
  // ===========================================================================
  // CRUD OPERATIONS
  // ===========================================================================

  /**
   * Create a new collection
   */
  async createCollection(
    libraryId: number,
    libraryType: LibraryType,
    name: string,
    parentCollection?: string
  ): Promise<ServiceResult<Collection>> {
    log.info('Creating collection', { libraryId, name, parentCollection });

    const result = await zoteroService.createCollection(
      libraryId,
      libraryType,
      name,
      parentCollection
    );

    if (result.success) {
      notify.success(`Collection "${name}" created`);
      log.info('Collection created', { key: result.data.key, name });
    } else {
      notify.error(`Failed to create collection: ${result.error}`);
    }

    return result;
  }

  /**
   * Rename a collection
   */
  async renameCollection(
    libraryId: number,
    libraryType: LibraryType,
    collection: Collection,
    newName: string
  ): Promise<ServiceResult<Collection>> {
    const oldName = collection.data.name;
    log.info('Renaming collection', { key: collection.key, oldName, newName });

    const result = await zoteroService.updateCollection(libraryId, libraryType, collection.key, {
      name: newName,
    });

    if (result.success) {
      notify.success(`Collection renamed to "${newName}"`);
      log.info('Collection renamed', { key: collection.key, newName });
    } else {
      notify.error(`Failed to rename collection: ${result.error}`);
    }

    return result;
  }

  /**
   * Move a collection to a new parent
   */
  async moveCollection(
    libraryId: number,
    libraryType: LibraryType,
    collection: Collection,
    newParentKey: string | false
  ): Promise<ServiceResult<Collection>> {
    log.info('Moving collection', {
      key: collection.key,
      newParent: newParentKey || 'root',
    });

    const result = await zoteroService.updateCollection(libraryId, libraryType, collection.key, {
      parentCollection: newParentKey,
    });

    if (result.success) {
      const destination = newParentKey ? 'new location' : 'root level';
      notify.success(`Collection moved to ${destination}`);
      log.info('Collection moved', { key: collection.key, newParent: newParentKey });
    } else {
      notify.error(`Failed to move collection: ${result.error}`);
    }

    return result;
  }

  /**
   * Delete a collection
   */
  async deleteCollection(
    libraryId: number,
    libraryType: LibraryType,
    collection: Collection
  ): Promise<ServiceResult<void>> {
    const name = collection.data.name;
    log.info('Deleting collection', { key: collection.key, name });

    const result = await zoteroService.deleteCollection(libraryId, libraryType, collection.key);

    if (result.success) {
      notify.success(`Collection "${name}" deleted`);
      log.info('Collection deleted', { key: collection.key });
    } else {
      notify.error(`Failed to delete collection: ${result.error}`);
    }

    return result;
  }

  // ===========================================================================
  // SPECIAL OPERATIONS
  // ===========================================================================

  /**
   * Refresh collections list
   */
  async refreshCollections(
    libraryId: number,
    libraryType: LibraryType
  ): Promise<ServiceResult<Collection[]>> {
    log.info('Refreshing collections', { libraryId });

    const result = await zoteroService.getCollections(libraryId, libraryType);

    if (result.success) {
      notify.success('Collections refreshed');
      log.info('Collections refreshed', { count: result.data.length });
    } else {
      notify.error(`Failed to refresh collections: ${result.error}`);
    }

    return result;
  }

  /**
   * Empty trash
   */
  async emptyTrash(libraryId: number, libraryType: LibraryType): Promise<ServiceResult<void>> {
    log.info('Emptying trash', { libraryId });

    const result = await zoteroService.emptyTrash(libraryId, libraryType);

    if (result.success) {
      notify.success('Trash emptied');
      log.info('Trash emptied');
    } else {
      notify.error(`Failed to empty trash: ${result.error}`);
    }

    return result;
  }

  // ===========================================================================
  // TREE UTILITIES
  // ===========================================================================

  /**
   * Build collection tree from flat list
   */
  buildTree(collections: Collection[]): CollectionNode[] {
    return buildCollectionTree(collections);
  }

  /**
   * Flatten collection tree for rendering
   */
  flattenTree(nodes: CollectionNode[], expandedKeys?: Set<string>): CollectionNode[] {
    return flattenCollectionTree(nodes, expandedKeys);
  }

  /**
   * Find a collection in the tree
   */
  findInTree(nodes: CollectionNode[], key: string): CollectionNode | null {
    return findCollectionInTree(nodes, key);
  }

  /**
   * Get ancestor keys
   */
  getAncestors(collections: Collection[], key: string): string[] {
    return getAncestorKeys(collections, key);
  }

  /**
   * Get descendant keys
   */
  getDescendants(collections: Collection[], key: string): string[] {
    return getDescendantKeys(collections, key);
  }

  /**
   * Check if one collection is ancestor of another
   */
  isAncestorOf(
    collections: Collection[],
    potentialAncestor: string,
    potentialDescendant: string
  ): boolean {
    const ancestors = this.getAncestors(collections, potentialDescendant);
    return ancestors.includes(potentialAncestor);
  }

  /**
   * Get collection path as string
   */
  getCollectionPath(collections: Collection[], key: string): string {
    const map = new Map(collections.map((c) => [c.key, c]));
    const path: string[] = [];

    let current = map.get(key);
    while (current) {
      path.unshift(current.data.name);
      current = current.data?.parentCollection ? map.get(current.data.parentCollection) : undefined;
    }

    return path.join(' / ');
  }

  // ===========================================================================
  // VALIDATION
  // ===========================================================================

  /**
   * Validate collection name
   */
  validateName(name: string): ActionResult {
    const trimmed = name.trim();

    if (!trimmed) {
      return { success: false, message: 'Collection name cannot be empty' };
    }

    if (trimmed.length > 255) {
      return { success: false, message: 'Collection name is too long' };
    }

    return { success: true, message: 'Valid collection name' };
  }

  /**
   * Check if collection can be moved to target
   */
  canMoveTo(
    collections: Collection[],
    collectionKey: string,
    targetKey: string | null
  ): ActionResult {
    // Can always move to root
    if (!targetKey) {
      return { success: true, message: 'Can move to root' };
    }

    // Cannot move to self
    if (collectionKey === targetKey) {
      return { success: false, message: 'Cannot move collection into itself' };
    }

    // Cannot move to descendant
    const descendants = this.getDescendants(collections, collectionKey);
    if (descendants.includes(targetKey)) {
      return { success: false, message: 'Cannot move collection into its descendant' };
    }

    return { success: true, message: 'Can move to target' };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

/** Singleton instance */
export const collectionActions = new CollectionActionsService();

/** Export class for testing */
export { CollectionActionsService };

export default collectionActions;
